import { useAuth } from '@/contexts/auth-context';
import { StandardDashboard } from '@/components/dashboard/standard-dashboard';
import { ProDashboard } from '@/components/dashboard/pro-dashboard';
import { HireDashboard } from '@/components/dashboard/hire-dashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { ConnectionsCard } from '@/components/dashboard/connections-card';
import { ArrowRight, Plus, User, Star, Briefcase, Users, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export default function DashboardPage() {
  const { user, currentMode, hasLicense } = useAuth();
  const navigate = useNavigate();
  const [careerData, setCareerData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (user) {
      fetchCareerData();
    }
  }, [user]);

  const fetchCareerData = async () => {
    if (!user) return;

    try {
      // Fetch latest career path
      const { data: pathData, error: pathError } = await supabase
        .from('career_paths')
        .select('*')
        .eq('profile_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // Changed from single() to maybeSingle()

      // Fetch current experience
      const { data: expData, error: expError } = await supabase
        .from('experiences')
        .select('*')
        .eq('profile_id', user.id)
        .eq('current', true)
        .maybeSingle(); // Changed from single() to maybeSingle()

      setCareerData({
        currentRole: {
          title: expData?.position || 'Not Set',
          company: expData?.company_name
        },
        targetRole: {
          title: pathData?.target_role || 'Not Set',
          timeframe: pathData?.timeframe || '2 years'
        },
        progress: pathData?.match_score || 0,
        milestones: pathData?.milestones || [
          'Complete advanced certifications',
          'Lead a major project',
          'Mentor junior team members'
        ]
      });
    } catch (error) {
      console.error('Error fetching career data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Immediately redirect to admin dashboard if in admin mode
  if (currentMode === 'admin') {
    navigate('/admin', { replace: true });
    return null;
  }
  
  // Show upgrade card if they don't have the required license
  if (currentMode !== 'standard' && !hasLicense(currentMode)) {
    return (
      <div className="container max-w-6xl py-8 px-4 md:px-6">
        <Card className="border shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You need a {currentMode === 'hire' ? 'recruiter' : 'pro'} license to access this feature.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/pricing">Upgrade Now</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const handleBrowseJobs = () => {
    navigate('/loops');
  };
  
  return (
    <div className="container max-w-6xl py-6 px-4 md:px-6">
      {currentMode === 'hire' ? (
        <HireDashboard />
      ) : currentMode === 'pro' ? (
        <ProDashboard />
      ) : (
        <StandardDashboard />
      )}
    </div>
  );
}
